import { CommonModule } from "@angular/common";
import { ChangeDetectorRef, Component, Input, TemplateRef, ViewChild } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { AvatarModule } from "primeng/avatar";
import { ButtonModule } from "primeng/button";
import { CheckboxChangeEvent, CheckboxModule } from "primeng/checkbox";
import { InputGroupModule } from "primeng/inputgroup";
import { InputGroupAddonModule } from "primeng/inputgroupaddon";
import { SkeletonModule } from "primeng/skeleton";
import { TabsModule } from "primeng/tabs";
import { TooltipModule } from "primeng/tooltip";
import { ButtonComponent } from "../../../../../@core/components/ui/button/button.component";
import { DialogComponent } from "../../../../../@core/components/ui/dialog/dialog.component";
import { CheckboxComponent } from "../../../../../@core/components/ui/checkbox/checkbox.component";
import { DeleteConfirmationComponent } from "../../../../../@core/components/ui/delete-confirmation/delete-confirmation.component";
import { IFile, IKnowledgeMapping, LINKED_DOCUMENT_TYPE, IAttachment } from "../../../../../@core/models/model";
import { ToastService } from "../../../../../@core/services/toast.service";
import { DataTableComponent } from "../../../../../@shared/components/data-table/data-table.component";
import { SearchBarComponent } from "../../../../../@shared/components/search-bar/search-bar.component";
import { TableColumn, TableConfig } from "../../../../../@shared/models/table.models";
import { DateFormatterPipe } from "../../../../../@shared/pipes/dateFormater.pipe";
import { SanitizeHtmlPipe } from "../../../../../@shared/pipes/sanitizeHtml.pipe";
import { FilterPipe } from "../../../../../pipes/filter.pipe";
import { FileSizePipe } from "../../../../../shared/pipes/file-size.pipe";
import { KNOWLEDGE_SCOPE } from "../../../../models/contract-management.model";
import { IDocumentType } from "../../../../models/settings.model";
import { KnowledgeMappingService } from "../../../../services/knowledge-mapping.service";
import { IKnowledgeBaseStore, KnowledgeBaseService } from "../../../../services/knowledgebase.service";
import { RepositoryService } from "../../../../services/repository.service";
import { SkeletonService } from "../../../../services/skeleton.service";
import { KNOWLEDGE_BASE_SVG } from "../../knowledgebase/knowledge-base-svg";
import { DocumentTypesListComponent } from "../../document-types-list/document-types-list.component";
import { DocumentPreviewComponent } from "../../../../../@shared/components/document-preview/document-preview.component";
import { UserNameDisplayComponent } from 'src/app/@shared/components/user-name-display';

@Component({
  selector: 'app-knowledge-mapping',
  imports: [
    SkeletonModule,
    CommonModule,
    ButtonModule,
    InputGroupModule,
    InputGroupAddonModule,
    FormsModule,
    DateFormatterPipe,
    DataTableComponent,
    AvatarModule,
    TabsModule,
    CheckboxModule,
    SanitizeHtmlPipe,
    TooltipModule,
    FileSizePipe,
    SearchBarComponent,
    ButtonComponent,
    DialogComponent,
    CheckboxComponent,
    DocumentTypesListComponent,
    DeleteConfirmationComponent,
    DocumentPreviewComponent,
    UserNameDisplayComponent,
  ],
  standalone: true,
  templateUrl: './knowledge-mapping.component.html',
  styleUrl: './knowledge-mapping.component.scss'
})
export class KnowledgeMappingComponent {

  @Input() projectId: string | null = null;
  @Input() scope: KNOWLEDGE_SCOPE = KNOWLEDGE_SCOPE.WORKSPACE;

  documentTypes: (IDocumentType & { mappingUpdatedAt: Date | null })[] = [];
  filteredDocTypes: (IDocumentType & { mappingUpdatedAt: Date | null })[] = [];
  searchDocTypes: string = '';
  selectedDocumentType: (IDocumentType & { mappingUpdatedAt: Date | null }) | null = null;
  documentTypeLoading = false;

  linkedDocs: IKnowledgeMapping[] = [];
  filteredLinkedDocs: IKnowledgeMapping[] = [];
  searchLinkedDocs: string = '';
  selectedLinkedDocs: IKnowledgeMapping[] = [];
  selectedKbFiles: any[] = [];
  selectedKbFilesForMapping: IFile[] = [];
  linkedDocsLoading = false;
  selectedKnowledgeBase: IKnowledgeBaseStore | null = null;
  downloadLoading = false;
  kbStores: IKnowledgeBaseStore[] = [];
  filteredKbStores: IKnowledgeBaseStore[] = [];
  searchKbStores: string = '';
  kbFiles: IFile[] = [];
  filteredKbFiles: IFile[] = [];
  searchKbFiles: string = '';
  mappingDialogLoading = false;
  showMappingDialog = false;
  selectedKbInMapping: IKnowledgeBaseStore | null = null;
  filteredKbInMapping: IKnowledgeBaseStore['files'][0][] = [];
  selectedFileName: string = '';

  skeletonCount: number = 1;
  filterPipe = new FilterPipe()
  SVGS = KNOWLEDGE_BASE_SVG;
  LINKED_DOCUMENT_TYPE = LINKED_DOCUMENT_TYPE
  activeIndex = 0;

  tabs = [
    {
      title: 'Knowledge base',
      type: LINKED_DOCUMENT_TYPE.KNOWLEDGE_BASE_STORE
    },
    {
      title: 'Files',
      type: LINKED_DOCUMENT_TYPE.FILE
    }
  ]

  selectedKbStores: IKnowledgeBaseStore[] = [];
  selectedItemsIdsSet = new Set<string>();

  tableColumns: TableColumn[] = [];
  kbStoreFileColumns: TableColumn[] = [];
  tableConfig: TableConfig = {};
  tableSelection: any[] = [];

  // Pagination state 
  linkedDocsPaginationState: number = 0;

  @ViewChild(DataTableComponent) dataTable!: DataTableComponent;

  @ViewChild('nameTemplate') nameTemplateRef!: TemplateRef<any>;
  @ViewChild('addedByTemplate') addedByTemplateRef!: TemplateRef<any>;
  @ViewChild('fileSizeTemplate') fileSizeTemplateRef!: TemplateRef<any>;
  @ViewChild('dateTemplate') dateTemplateRef!: TemplateRef<any>;
  @ViewChild('actionsTemplate') actionsTemplateRef!: TemplateRef<any>;

  @ViewChild('kbFileNameTemplate') kbFileNameTemplateRef!: TemplateRef<any>;
  @ViewChild('kbFileAddedByTemplate') kbFileAddedByTemplateRef!: TemplateRef<any>;
  @ViewChild('kbFileSizeTemplate') kbFileSizeTemplateRef!: TemplateRef<any>;
  @ViewChild('kbFileDateTemplate') kbFileDateTemplateRef!: TemplateRef<any>;
  @ViewChild('kbFileActionsTemplate') kbFileActionsTemplateRef!: TemplateRef<any>;

  showDelinkFilesDialog = false;
  delinkFileToRemove: IKnowledgeMapping | null = null;
  showDelinkKbFileDialog = false;
  delinkKbFileToRemove: any | null = null;
  showDelinkKbFilesDialog = false;

  // Document preview properties
  public selectedFile: IFile | null = null;
  showDocumentPreview: boolean = false;
  selectedDocumentFile: IAttachment | null = null;
  selectedDocumentId: string | null = null;

  constructor(
    private knowledgeMappingService: KnowledgeMappingService,
    private repositoryService: RepositoryService,
    private knowledgeBaseService: KnowledgeBaseService,
    private skeletonService: SkeletonService,
    private cdr: ChangeDetectorRef,
    private toast: ToastService
  ) {

  }

  temp = true

  ngOnInit() {
    this.setupResizeObserver();
    this.initializeTableConfig();
    this.getDocumentTypes()
  }

  ngOnDestroy(): void {
    this.cleanupResizeObserver();
  }

  ngAfterViewInit(): void {
  }

  get templatesMap(): { [key: string]: TemplateRef<any> } {
    return {
      'nameTemplate': this.nameTemplateRef,
      'addedByTemplate': this.addedByTemplateRef,
      'fileSizeTemplate': this.fileSizeTemplateRef,
      'dateTemplate': this.dateTemplateRef,
      'actionsTemplate': this.actionsTemplateRef,
      'kbFileNameTemplate': this.kbFileNameTemplateRef,
      'kbFileAddedByTemplate': this.kbFileAddedByTemplateRef,
      'kbFileSizeTemplate': this.kbFileSizeTemplateRef,
      'kbFileDateTemplate': this.kbFileDateTemplateRef,
      'kbFileActionsTemplate': this.kbFileActionsTemplateRef
    };
  }

  get currentTableColumns(): TableColumn[] {
    return this.selectedKnowledgeBase ? this.kbStoreFileColumns : this.tableColumns;
  }

  initializeTableConfig() {
    this.tableColumns = [
      {
        field: 'linkedDocument.name',
        header: 'Name',
        type: 'custom',
        template: 'nameTemplate',
        sortable: true
      },
      {
        field: 'createdBy.fullName',
        header: 'Added By',
        type: 'custom',
        template: 'addedByTemplate',
        sortable: true
      },
      {
        field: 'linkedDocument.size',
        header: 'File Size',
        type: 'custom',
        template: 'fileSizeTemplate',
        sortable: true
      },
      {
        field: 'createdAt',
        header: 'Added on',
        type: 'custom',
        template: 'dateTemplate',
        sortable: true
      },
      {
        field: 'actions',
        header: 'Actions',
        type: 'custom',
        template: 'actionsTemplate',
        sortable: false
      }
    ];

    this.kbStoreFileColumns = [
      {
        field: 'file.name',
        header: 'Name',
        type: 'custom',
        template: 'kbFileNameTemplate',
        sortable: true
      },
      {
        field: 'createdBy.fullName',
        header: 'Added By',
        type: 'custom',
        template: 'kbFileAddedByTemplate',
        sortable: true
      },
      {
        field: 'file.size',
        header: 'File Size',
        type: 'custom',
        template: 'kbFileSizeTemplate',
        sortable: true
      },
      {
        field: 'uploadedAt',
        header: 'Added on',
        type: 'custom',
        template: 'kbFileDateTemplate',
        sortable: true
      },
      {
        field: 'actions',
        header: 'Actions',
        type: 'custom',
        template: 'kbFileActionsTemplate',
        sortable: false
      }
    ];

    this.tableConfig = {
      selectable: true,
      paginator: true,
      rows: 5,
      rowsPerPageOptions: [5, 10, 15, 20],
      loading: this.linkedDocsLoading
    };
  }

  getDocumentTypes() {
    this.documentTypeLoading = true;
    const params = {
      scope: this.scope,
      ...(this.scope == KNOWLEDGE_SCOPE.PROJECT ? { projectId: this.projectId as string } : {})
    };
    this.knowledgeMappingService.getDocumentTypesForKnowledgeMapping(params).subscribe(res => {
      if (res && res.success) {
        this.documentTypes = res.data || [];
        this.filteredDocTypes = res.data || [];
        const selectedDocType = this.documentTypes.find(item => item._id == this.selectedDocumentType?._id)
        if (this.documentTypes.length > 0) {
          this.selectDocumentType(selectedDocType ?? this.documentTypes[0]);
          this.getLinkedDocs(selectedDocType ?? this.documentTypes[0]);
        }
      }
      this.documentTypeLoading = false
    })
  }

  getLinkedDocs(selectedDocumentType: (IDocumentType & { mappingUpdatedAt: Date | null })) {
    if (!selectedDocumentType) return;
    this.linkedDocsLoading = true
    this.clearTableSelection();
    this.selectedKnowledgeBase = null

    this.updateTableConfig();
    const params = {
      documentTypeId: selectedDocumentType._id,
      scope: this.scope,
      ...(this.scope == KNOWLEDGE_SCOPE.PROJECT ? { project: this.projectId as string } : {})
    }
    this.knowledgeMappingService.getKnowledgeMappingsByDocumentType(params).subscribe(res => {
      if (res && res.success) {
        this.linkedDocs = res.data || [];
        this.filteredLinkedDocs = res.data || [];
        this.searchLinkedDocs = ''
      }
      this.linkedDocsLoading = false
      this.updateTableConfig();
    })
  }

  getFiles() {
    return new Promise((resolve, reject) => {
      const params = {
        scope: this.scope,
        ...(this.scope == KNOWLEDGE_SCOPE.PROJECT ? { project: this.projectId as string } : {})
      }
      this.repositoryService.getFiles(params).subscribe((res) => {
        if (res && res.success) {
          this.kbFiles = res.data
          this.filteredKbFiles = [...this.kbFiles]
          resolve(true)
        } else {
          reject(false)
        }
      })
    })
  }

  getKnowledgeBaseStore() {
    return new Promise((resolve, reject) => {
      const filters = {
        scope: this.scope,
        ...(this.scope == KNOWLEDGE_SCOPE.PROJECT ? { project: this.projectId as string } : {})
      }
      this.knowledgeBaseService.getKnowledgeBaseStore(filters).subscribe((res) => {
        if (res && res.success) {
          this.kbStores = res.data.stores;
          this.filteredKbStores = [...this.kbStores]
          resolve(true)
        } else {
          reject(false)
        }
      })
    })
  }

  mapFiles() {
    if (!this.selectedDocumentType?._id) return
    this.knowledgeMappingService.createKnowledgeMappings({
      scope: this.scope,
      ...(this.scope == KNOWLEDGE_SCOPE.PROJECT ? { project: this.projectId as string } : {}),
      fileIds: this.kbFiles.filter(item => this.selectedItemsIdsSet.has(item._id)).map(item => item._id),
      kbStoreIds: this.kbStores.filter(item => this.selectedItemsIdsSet.has(item._id)).map(item => item._id),
      documentTypeId: this.selectedDocumentType._id
    }).subscribe(res => {
      if (res && res.success) {
        this.hideMappingDialog()
        this.toast.success("Files mapped successfully")
        this.getDocumentTypes()
      }
    })
  }

  delinkFiles(file?: IKnowledgeMapping) {
    this.delinkFileToRemove = file || null;
    this.showDelinkFilesDialog = true;
  }

  confirmDelinkFiles() {
    const mappingIds = this.delinkFileToRemove
      ? [this.delinkFileToRemove._id]
      : this.selectedLinkedDocs.map(doc => doc._id);

    const payload = {
      mappingIds,
      documentTypeId: this.selectedDocumentType?._id ?? '',
      scope: this.scope,
      ...(this.scope == KNOWLEDGE_SCOPE.PROJECT ? { project: this.projectId as string } : {})
    };

    this.knowledgeMappingService.deleteKnowledgeMappings(payload)
      .subscribe(res => {
        if (res && res.success && this.selectedDocumentType) {
          this.toast.success(this.delinkFileToRemove
            ? "File delinked successfully"
            : "Files delinked successfully");
          this.getLinkedDocs(this.selectedDocumentType);
          this.showDelinkFilesDialog = false;
        }
      });
  }

  getTableData() {
    if (this.selectedKnowledgeBase) {
      const files = this.selectedKnowledgeBase.files || [];
      const term = (this.searchLinkedDocs || '').trim().toLowerCase();
      if (!term) return files;
      return files.filter((item: any) => {
        const name = item?.file?.name ? String(item.file.name).toLowerCase() : '';
        return name.includes(term);
      });
    }
    return this.filteredLinkedDocs;
  }


  selectDocumentType(documentType: any) {
    this.selectedDocumentType = documentType
    this.getLinkedDocs(documentType)
  }

  closeDocumentPreview() {
    this.selectedFile = null;
    this.selectedDocumentFile = null;
    this.selectedDocumentId = null;
    this.showDocumentPreview = false;
  }

  viewKbFile(kbFileItem: any) {
    this.selectedFile = kbFileItem.file;
    this.selectedFileName = kbFileItem.file.name;
    this.selectedDocumentFile = kbFileItem.file.attachment as IAttachment;
    this.selectedDocumentId = kbFileItem.file._id;
    this.showDocumentPreview = true;
  }

  delinkKbFile(kbFileItem: any) {
    this.delinkKbFileToRemove = kbFileItem;
    this.showDelinkKbFileDialog = true;
  }

  viewFile(fileItem: any) {
    this.selectedFile = fileItem;
    this.selectedFileName = fileItem.linkedDocument?.name || '';
    this.selectedDocumentFile = fileItem.linkedDocument.attachment as IAttachment;
    this.selectedDocumentId = fileItem.linkedDocument._id;
    this.showDocumentPreview = true;
  }

  confirmDelinkKbFile() {
    if (this.selectedKnowledgeBase && this.delinkKbFileToRemove) {
      this.knowledgeBaseService.delinkFiles([this.delinkKbFileToRemove.file._id], this.selectedKnowledgeBase._id)
        .subscribe(res => {
          if (res && res.success) {
            this.toast.success("File delinked successfully");
            this.knowledgeBaseService.getKnowledgeBaseStoreById(this.selectedKnowledgeBase!._id)
              .subscribe(kbRes => {
                if (kbRes && kbRes.success) {
                  this.selectedKnowledgeBase = kbRes.data;
                  this.cdr.detectChanges();
                }
              });
            this.showDelinkKbFileDialog = false;
          }
        });
    }
  }

  delinkKbFiles() {
    this.showDelinkKbFilesDialog = true;
  }

  confirmDelinkKbFiles() {
    if (this.selectedKnowledgeBase && this.selectedKbFiles.length > 0) {
      const fileIds = this.selectedKbFiles.map(file => file.file._id);
      this.knowledgeBaseService.delinkFiles(fileIds, this.selectedKnowledgeBase._id)
        .subscribe(res => {
          if (res && res.success) {
            this.toast.success(`${this.selectedKbFiles.length} files delinked successfully`);
            this.clearTableSelection();
            this.knowledgeBaseService.getKnowledgeBaseStoreById(this.selectedKnowledgeBase!._id)
              .subscribe(kbRes => {
                if (kbRes && kbRes.success) {
                  this.selectedKnowledgeBase = kbRes.data;
                  this.cdr.detectChanges();
                }
              });
            this.showDelinkKbFilesDialog = false;
          }
        });
    }
  }

  toggleKb(knowledgeBase?: IKnowledgeMapping) {
    this.clearTableSelection();
    if (knowledgeBase) {
      if (this.dataTable) {
        this.linkedDocsPaginationState = this.dataTable.first;
      }

      const kbId = (knowledgeBase.linkedDocument as IKnowledgeBaseStore)?._id || (knowledgeBase.linkedDocument as string);

      this.selectedKnowledgeBase = knowledgeBase.linkedDocument as IKnowledgeBaseStore;
      this.updateTableConfig();
      this.restorePagination(0);
      this.cdr.detectChanges();

      this.knowledgeBaseService.getKnowledgeBaseStoreById(kbId).subscribe(res => {
        if (res.success) {
          this.selectedKnowledgeBase = res.data;
        }
      });
    } else {
      this.selectedKnowledgeBase = null;
      this.updateTableConfig();
      this.restorePagination(this.linkedDocsPaginationState);
    }
    this.cdr.detectChanges();
  }

  toggleKbInMapping(kb?: IKnowledgeBaseStore) {
    if (kb) {
      this.selectedKbInMapping = kb
      this.filteredKbInMapping = [...(kb.files || [])]
    } else {
      this.selectedKbInMapping = null
      this.filteredKbInMapping = []
    }
  }

  getSelectAllValue(type: 'kbStores' | 'kbFiles'): boolean {
    if (type === 'kbStores') {
      if (this.selectedKbInMapping) {
        return this.filteredKbInMapping.every(item => this.selectedItemsIdsSet.has(item.file._id))
      }
      return this.filteredKbStores.length > 0 && this.filteredKbStores.every(item => this.selectedItemsIdsSet.has(item._id));
    } else {
      return this.filteredKbFiles.length > 0 && this.filteredKbFiles.every(item => this.selectedItemsIdsSet.has(item._id));
    }
  }

  toggleSelect(type: 'kbStores' | 'kbFiles', item: IKnowledgeBaseStore | IFile) {
    if (type === 'kbStores') {
      if (this.selectedItemsIdsSet.has(item._id)) {
        this.selectedItemsIdsSet.delete(item._id)
        this.selectedKbStores = this.selectedKbStores.filter(selectedItem => selectedItem?._id !== item._id)
      } else {
        this.selectedItemsIdsSet.add(item._id)
        this.selectedKbStores.push(item as IKnowledgeBaseStore)
      }
    } else {
      if (this.selectedItemsIdsSet.has(item._id)) {
        this.selectedItemsIdsSet.delete(item._id)
        this.selectedKbFilesForMapping = this.selectedKbFilesForMapping.filter(selectedItem => selectedItem?._id !== item._id)
      } else {
        this.selectedItemsIdsSet.add(item._id)
        this.selectedKbFilesForMapping.push(item as IFile)
      }
    }
  }

  toggleSelectAll(type: 'kbStores' | 'kbFiles', event: CheckboxChangeEvent) {
    if (type === 'kbStores') {
      if (event.checked) {
        this.filteredKbStores.forEach(item => {
          this.selectedItemsIdsSet.add(item._id)
          this.selectedKbStores.push(item as IKnowledgeBaseStore)
        });
      } else {
        this.filteredKbStores.forEach(item => {
          this.selectedItemsIdsSet.delete(item._id)
          this.selectedKbStores = this.selectedKbStores.filter(selectedItem => selectedItem?._id !== item._id)
        });
      }
    }
    else {
      if (event.checked) {
        this.filteredKbFiles.forEach(item => {
          this.selectedItemsIdsSet.add(item._id)
          this.selectedKbFilesForMapping.push(item as IFile)
        });
      } else {
        this.filteredKbFiles.forEach(item => {
          this.selectedItemsIdsSet.delete(item._id)
          this.selectedKbFilesForMapping = this.selectedKbFilesForMapping.filter(selectedItem => selectedItem?._id !== item._id)
        });
      }
    }
  }

  openMappingDialog() {
    this.showMappingDialog = true
    if (this.kbFiles.length > 0 || this.kbStores.length > 0) {
      return
    }
    this.mappingDialogLoading = true
    Promise.all([this.getFiles(), this.getKnowledgeBaseStore()]).then(() => {
      this.mappingDialogLoading = false
      this.cdr.detectChanges();
    }).catch(() => {
      this.hideMappingDialog()
    })
  }

  onTabChange(event: string | number | undefined): void {
    if (event !== undefined && event !== null) {
      this.activeIndex = typeof event === 'string' ? parseInt(event, 10) : event;
    }
  }

  hideMappingDialog() {
    this.selectedItemsIdsSet.clear()

    this.selectedKbStores = []
    this.selectedKbFilesForMapping = []
    this.activeIndex = 0

    this.showMappingDialog = false
    this.mappingDialogLoading = false

    this.filteredKbStores = [...this.kbStores]
    this.searchKbStores = ''

    this.filteredKbFiles = [...this.kbFiles]
    this.searchKbFiles = ''
  }

  private setupResizeObserver() {
    this.skeletonService.setupResizeObserver(() => {
      this.calculateSkeletonCount();
      this.cdr.detectChanges();
    });
  }

  private cleanupResizeObserver() {
    this.skeletonService.cleanupResizeObserver();
  }

  private calculateSkeletonCount() {
    const itemHeight = 5 * 16;
    const gap = 4 * 16;
    const padding = 4 * 16;

    this.skeletonCount = this.skeletonService.calculateSkeletonCount(itemHeight, gap, padding);
  }

  getSkeletonCount(): number[] {
    return this.skeletonService.getSkeletonArray(this.skeletonCount);
  }

  private getFiltered<T>(searchValue: string, sourceArray: T[], filterProperty: string): T[] {
    return this.filterPipe.transform(sourceArray, filterProperty, searchValue);
  }

  onDocTypesSearch() {
    this.filteredDocTypes = this.getFiltered(this.searchDocTypes, this.documentTypes, 'name');
  }

  onLinkedDocsSearch() {
    const term = (this.searchLinkedDocs || '').trim().toLowerCase();
    if (!term) {
      this.filteredLinkedDocs = [...this.linkedDocs];
      return;
    }
    this.filteredLinkedDocs = this.linkedDocs.filter((mapping: IKnowledgeMapping) => {
      const linked: any = mapping.linkedDocument as any;
      const name = linked && typeof linked === 'object' && linked.name ? String(linked.name).toLowerCase() : '';
      const creator = (mapping as any).createdBy && (mapping as any).createdBy.fullName
        ? String((mapping as any).createdBy.fullName).toLowerCase()
        : '';
      return name.includes(term) || creator.includes(term);
    });
  }

  getKbStoreFileCount(store: IKnowledgeBaseStore): number {
    return store.files?.length ?? store.size ?? 0;
  }

  onKbStoresSearch() {
    if (this.selectedKbInMapping) {
      this.filteredKbInMapping = this.getFiltered(this.searchKbStores, this.selectedKbInMapping.files as any, 'file.name');
    } else {
      this.filteredKbStores = this.getFiltered(this.searchKbStores, this.kbStores, 'name');
    }
  }

  onKbFilesSearch() {
    this.filteredKbFiles = this.getFiltered(this.searchKbFiles, this.kbFiles, 'name');
  }

  onTableSelectionChange(selection: any[]) {
    if (this.selectedKnowledgeBase) {
      this.selectedKbFiles = selection;
    } else {
      this.selectedLinkedDocs = selection;
    }
    this.tableSelection = selection;
    this.updateTableConfig();
  }

  clearTableSelection() {
    this.selectedKbFiles = [];
    this.selectedLinkedDocs = [];
    this.tableSelection = [];
    this.updateTableConfig();
  }

  getTableMaxHeight(): string {
    const hasBulkActions = (!this.selectedKnowledgeBase && this.selectedLinkedDocs.length > 0) ||
      (this.selectedKnowledgeBase && this.selectedKbFiles.length > 0);
    return hasBulkActions ? 'calc(100vh - 30.5rem)' : 'calc(100vh - 26.5rem)';
  }

  updateTableConfig() {
    this.tableConfig = {
      ...this.tableConfig,
      selectable: true,
      loading: this.linkedDocsLoading
    };
  }

  restorePagination(pageIndex: number) {
    if (this.dataTable) {
      this.dataTable.first = pageIndex;
    }
  }

  downloadSelectedItems() {
    let kbIds: string[] = [];
    let fileIds: string[] = [];

    if (this.selectedKnowledgeBase) {
      // When viewing a knowledge base, selectedKbFiles contains files from that KB
      fileIds = this.selectedKbFiles.map(item => item.file._id);
    } else {
      // When viewing linked documents, separate knowledge bases and files
      this.selectedLinkedDocs.forEach(mapping => {
        if (mapping.linkedDocumentType === LINKED_DOCUMENT_TYPE.KNOWLEDGE_BASE_STORE) {
          const kb = mapping.linkedDocument as IKnowledgeBaseStore;
          kbIds.push(kb._id);
        } else if (mapping.linkedDocumentType === LINKED_DOCUMENT_TYPE.FILE) {
          const file = mapping.linkedDocument as IFile;
          fileIds.push(file._id);
        }
      });
    }

    // Only proceed if there are items to download
    if (kbIds.length === 0 && fileIds.length === 0) {
      this.toast.error('Please select items to download');
      return;
    }
    this.downloadLoading = true;
    this.knowledgeMappingService.downloadKbAndFile(kbIds, fileIds).subscribe({
      next: (blob: Blob) => {
        if (blob && blob.size > 0) {
          // Create download link
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `${this.selectedDocumentType?.name ?? ""} knowledge mapping.zip`;

          // Trigger download
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          // Clean up
          window.URL.revokeObjectURL(url);
          this.downloadLoading = false;
          this.toast.success('Files downloaded successfully');
        } else {
          this.toast.error('Failed to download files');
        }
      },
      error: (error) => {
        console.error('Download error:', error);
        this.toast.error('Failed to download files');
        this.downloadLoading = false;
      }
    });
  }
}
